import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ACTIVE_USER_ID_API_URL_PARAM, ORG_API_URL_PARAM, ORG_SPACE_API_URL_PARAM } from '@y42/model';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { UserQuery } from '@y42/user/state';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class OrgApiUrlInterceptor implements HttpInterceptor {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
  ) {}

  private readonly orgId$ = this.orgQuery.activeOrg$.pipe(map(({ id }) => id));
  private readonly spaceId$ = this.orgSpaceQuery.active$.pipe(
    map((active) => (active ? active.id : 'org-space-not-found')),
  );
  private readonly userId$ = this.userQuery.activeUserId$.pipe(map((id) => id));

  private readonly paramToValue: Record<string, Observable<string | number>> = {
    [ORG_API_URL_PARAM]: this.orgId$,
    [ORG_SPACE_API_URL_PARAM]: this.spaceId$,
    [ACTIVE_USER_ID_API_URL_PARAM]: this.userId$,
  };

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    if (req.url.startsWith('assets')) {
      return next.handle(req);
    }

    return this.buildURL(req.url).pipe(
      take(1),
      map((url) => req.clone({ url })),
      switchMap((modifiedReq) => next.handle(modifiedReq)),
    );
  }

  private buildURL(reqUrl: string) {
    const apiUrl = decodeURI(reqUrl);
    const apiUrl$ = of(apiUrl);

    if (!Object.keys(this.paramToValue).some((param) => apiUrl.includes(param))) {
      return apiUrl$;
    }

    return Object.entries(this.paramToValue)
      .filter(([param]) => apiUrl.includes(param))
      .reduce(
        (url$, [param, value$]) => url$.pipe(switchMap((url) => this.replaceParamWithValue(url, param, value$))),
        apiUrl$,
      );
  }

  /**
   * Replaces ORG_API_URL_PARAM in the requested URL with its actual value
   * @param url the requested URL
   */
  private replaceParamWithValue(url: string, param: string, value$: Observable<string | number>) {
    return value$.pipe(map((value) => `${url.replace(param, encodeURI(`${value}`))}`));
  }
}
